import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SelectField } from "@/components/SelectField/SelectField";
import { InputField } from "@/components/InputField/InputField";
import { useAppSelector } from "@/redux/hooks";
import { scoreSelector } from "@/pages/Private/redux/score/score.slice";
import { useGetIndustriesQuery } from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { FundingCountries } from "@/enum/swarm.enum";

import {
	OverrideRule,
	OverrideRuleCriteria,
	OverrideRuleOperator,
	OverrideRuleAction,
	OverrideRuleLocation,
	NUMERIC_CRITERIA,
	NUMERIC_OPERATORS,
	CATEGORICAL_OPERATORS,
} from "../types/override-rules";

interface OverrideRuleRowProps {
	rule: OverrideRule;
	index: number;
	onChange: (rule: OverrideRule) => void;
	onDelete: () => void;
}

export const OverrideRuleRow: FunctionComponent<OverrideRuleRowProps> = ({
	rule,
	index,
	onChange,
	onDelete,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.overrideRules.${key}`);

	const { score } = useAppSelector(scoreSelector);
	const { data: industriesData } = useGetIndustriesQuery();

	const criteriaOptions = [
		{ title: ts("criteria.employees"), value: OverrideRuleCriteria.EMPLOYEES },
		{ title: ts("criteria.hqCountry"), value: OverrideRuleCriteria.HQ_COUNTRY },
		{ title: ts("criteria.industry"), value: OverrideRuleCriteria.INDUSTRY },
		{ title: ts("criteria.segment"), value: OverrideRuleCriteria.SEGMENT },
		{ title: ts("criteria.founded"), value: OverrideRuleCriteria.FOUNDED },
	];

	const isNumeric = NUMERIC_CRITERIA.includes(rule.criteria);

	const operatorOptions = useMemo(() => {
		if (isNumeric) {
			return NUMERIC_OPERATORS.map((op) => ({ title: ts(`operators.${op}`), value: op }));
		}

		return CATEGORICAL_OPERATORS.map((op) => ({ title: ts(`operators.${op}`), value: op }));
	}, [isNumeric, ts]);

	const countryOptions = FundingCountries.map((c) => ({ title: c, value: c }));

	const industryOptions = useMemo(
		() => (industriesData || []).map((ind) => ({ title: ind.name, value: ind.name })),
		[industriesData]
	);

	const segmentOptions = useMemo(
		() =>
			(score?.prospectCompanyCategories || []).map((cat) => ({
				title: cat.category,
				value: cat.category,
			})),
		[score]
	);

	const personaOptions = useMemo(
		() =>
			(score?.prospectPersonaCategories || []).map((cat) => ({
				title: cat.category,
				value: String(cat.id),
			})),
		[score]
	);

	const actionOptions = [
		{ title: ts("actions.ceo"), value: OverrideRuleAction.CEO },
		{ title: ts("actions.persona"), value: OverrideRuleAction.PERSONA },
		{ title: ts("actions.custom"), value: OverrideRuleAction.CUSTOM },
	];

	const locationOptions = [
		{ title: ts("locations.anywhere"), value: OverrideRuleLocation.ANYWHERE },
		{ title: ts("locations.hqCountry"), value: OverrideRuleLocation.HQ_COUNTRY },
		{ title: ts("locations.specific"), value: OverrideRuleLocation.SPECIFIC },
	];

	const handleCriteriaChange = (criteria: OverrideRuleCriteria) => {
		const newIsNumeric = NUMERIC_CRITERIA.includes(criteria);
		const newOperator = newIsNumeric ? OverrideRuleOperator.LESS_THAN : OverrideRuleOperator.IS;

		onChange({ ...rule, criteria, operator: newOperator, value: "", valueTo: undefined });
	};

	const getValueDropdownOptions = () => {
		switch (rule.criteria) {
			case OverrideRuleCriteria.HQ_COUNTRY:
				return countryOptions;
			case OverrideRuleCriteria.INDUSTRY:
				return industryOptions;
			case OverrideRuleCriteria.SEGMENT:
				return segmentOptions;
			default:
				return [];
		}
	};

	return (
		<div className="flex items-start gap-3 py-3 border-b border-border-light last:border-b-0 px-3">
			<div className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-white text-text-meta text-xs font-medium mt-1 shrink-0">
				{index + 1}
			</div>
			<div className="flex-1 flex flex-col gap-1.5">
				{/* IF line */}
				<div className="flex flex-wrap items-center gap-1.5">
					<span className="text-xs font-semibold text-brand-light">{ts("if")}</span>
					<div className="w-[250px]">
						<SelectField
							handleSelect={(v) => handleCriteriaChange(v as OverrideRuleCriteria)}
							hideEmptyValue
							options={criteriaOptions}
							selectedOption={rule.criteria}
						/>
					</div>
					<div className="w-[130px]">
						<SelectField
							handleSelect={(v) => onChange({ ...rule, operator: v as OverrideRuleOperator })}
							hideEmptyValue
							options={operatorOptions}
							selectedOption={rule.operator}
						/>
					</div>
					{isNumeric ? (
						<div className="flex gap-1.5 items-center">
							<div className="w-[80px]">
								<InputField
									className="-mb-3"
									handleChange={(e) => onChange({ ...rule, value: e.target.value })}
									name={`rule-${rule.id}-value`}
									placeholder={ts("valuePlaceholder")}
									value={rule.value}
								/>
							</div>
							{rule.operator === OverrideRuleOperator.BETWEEN && (
								<>
									<span className="text-xs text-text-meta">{ts("and")}</span>
									<div className="w-[80px]">
										<InputField
											className="-mb-3"
											handleChange={(e) => onChange({ ...rule, valueTo: e.target.value })}
											name={`rule-${rule.id}-valueTo`}
											placeholder={ts("valuePlaceholder")}
											value={rule.valueTo || ""}
										/>
									</div>
								</>
							)}
						</div>
					) : (
						<div className="w-[140px]">
							<SelectField
								handleSelect={(v) => onChange({ ...rule, value: v as string })}
								hideEmptyValue
								options={getValueDropdownOptions()}
								selectedOption={rule.value}
							/>
						</div>
					)}
				</div>

				{/* THEN line */}
				<div className="flex flex-wrap items-center gap-1.5">
					<span className="text-xs font-semibold text-brand-light">{ts("thenSearchFor")}</span>
					<div className="w-fit">
						<SelectField
							handleSelect={(v) => {
								const action = v as OverrideRuleAction;

								onChange({
									...rule,
									action,
									actionDetail: undefined,
									personaCategoryId: undefined,
								});
							}}
							hideEmptyValue
							options={actionOptions}
							selectedOption={rule.action}
						/>
					</div>
					{rule.action === OverrideRuleAction.PERSONA && (
						<div className="w-[180px]">
							<SelectField
								handleSelect={(v) => {
									const personaCategoryId = v ? Number(v) : undefined;
									const persona = score?.prospectPersonaCategories?.find(
										(c) => c.id === personaCategoryId
									);

									onChange({
										...rule,
										personaCategoryId,
										actionDetail: persona?.category,
									});
								}}
								hideEmptyValue
								options={personaOptions}
								selectedOption={rule.personaCategoryId ? String(rule.personaCategoryId) : null}
							/>
						</div>
					)}
					{rule.action === OverrideRuleAction.CUSTOM && (
						<div className="w-[160px]">
							<InputField
								handleChange={(e) => onChange({ ...rule, actionDetail: e.target.value })}
								name={`rule-${rule.id}-actionDetail`}
								placeholder={ts("customTitlePlaceholder")}
								value={rule.actionDetail || ""}
							/>
						</div>
					)}
					<span className="text-xs text-text-meta">{ts("in")}</span>
					<div className="w-[180px]">
						<SelectField
							handleSelect={(v) => {
								onChange({
									...rule,
									location: v as OverrideRuleLocation,
									locationDetail: undefined,
								});
							}}
							hideEmptyValue
							options={locationOptions}
							selectedOption={rule.location}
						/>
					</div>
					{rule.location === OverrideRuleLocation.SPECIFIC && (
						<div className="w-[130px]">
							<SelectField
								handleSelect={(v) => onChange({ ...rule, locationDetail: v as string })}
								hideEmptyValue
								options={countryOptions}
								selectedOption={rule.locationDetail || null}
							/>
						</div>
					)}
					<span className="text-xs text-text-meta">{ts("first")}</span>
				</div>
			</div>

			{/* Delete button */}
			<button
				className="mt-1 p-1.5 rounded hover:bg-red-50 text-text-meta hover:text-red-500 transition-colors shrink-0"
				title={ts("deleteRule")}
				onClick={onDelete}
			>
				<svg
					fill="none"
					height="14"
					stroke="currentColor"
					strokeWidth="1.5"
					viewBox="0 0 14 14"
					width="14"
				>
					<path d="M3 4h8M5 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1m1 0v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4" />
				</svg>
			</button>
		</div>
	);
};
